import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Results, Uniprot } from "../../../api/openapi-generator";
import { useAvailableSections } from "./use-available-sections";
import IJobSection from "../interfaces/IJobSection";
import ChangeEnum from "../interfaces/frontend/ChangeEnum";
import IResultInhibitor from "../interfaces/frontend/IResultInhibitor";
import { useFrontendResults } from "./use-frontend-results";

type ResultsContextType = ReturnType<typeof useProviderValue>;

const ResultsContext = createContext<ResultsContextType | undefined>(undefined);

export const useResultsContext = () => {
  const context = useContext(ResultsContext);
  if (context === undefined) {
    throw new Error("useResultsContext must be used within a ResultsContextProvider");
  }
  return context;
};

export function ResultsContextProvider({backendResults, uniprot, children}: {
  backendResults: Results,
  uniprot: Uniprot,
  children: ReactNode,
}) {
  const value = useProviderValue(backendResults, uniprot);
  return (
    <ResultsContext.Provider value={value}>
      {children}
    </ResultsContext.Provider>
  );
}

function useProviderValue(backendResults: Results, uniprot: Uniprot) {

  // State:

  const [inhibitorDiffFilter, setInhibitorDiffFilter] = useState<ChangeEnum | null>(null);
  const [selectedInhibitor, setSelectedInhibitor] = useState<IResultInhibitor | null>(null);

  // Custom hooks:

  const results = useFrontendResults(backendResults, uniprot);
  const availableSections = useAvailableSections(results, uniprot);
  const { selectedSections, selectSection, selectAllSections } = useSelectedSections(availableSections);

  // Effects:

  return {
    results,
    uniprot,
    availableSections,
    selectedSections, selectSection, selectAllSections,
    inhibitorDiffFilter, setInhibitorDiffFilter,
    selectedInhibitor, setSelectedInhibitor,
  };
}

function useSelectedSections(availableSections: IJobSection[]) {
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

  const selectAllSections = useCallback(() => {
    const entries = availableSections.map(section => [section.id, true] as const);
    setSelectedIds(Object.fromEntries(entries));
  }, [availableSections]);

  const selectSection = useCallback((id: string, selected: boolean) => {
    setSelectedIds(prev => ({
      ...prev,
      [id]: selected,
    }));
  }, []);

  useEffect(() => {
    selectAllSections();
  }, [availableSections, selectAllSections]);

  return {
    selectedSections: selectedIds,
    selectSection,
    selectAllSections,
  }
}